import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { decodeToken, TokenError } from '@pink/shared';

import type { TokenParseResult } from './token-parse-result.type.js';

/**
 * URL-Param-Schlüssel der Route `/cancel/:token`. Single Source of Truth für
 * Provider (`<Route path>`) und Consumer (`useParams`).
 */
const ROUTE_TOKEN_PARAM = 'token';

/**
 * Liefert das rohe 24-Zeichen-Base64URL-Token aus der Route oder `null`,
 * wenn der Route-Param fehlt (z. B. außerhalb der `/cancel/:token`-Route).
 * Nicht-validierender Zugriff — der Caller darf voraussetzen, dass die
 * Route bereits gegriffen hat.
 */
export function useRawCancelToken(): string | null {
  const params = useParams();
  const raw = params[ROUTE_TOKEN_PARAM];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

interface UseTokenFromRouteOptions {
  /**
   * Referenz-Zeitpunkt für den Plausibility-Check im Shared-Codec.
   * Default `Date.now()`; Tests übergeben einen festen Wert.
   */
  readonly nowMs?: number;
}

/**
 * Decodiert den URL-Path-Param `token` einmalig pro Routenwechsel und liefert
 * ein diskriminiertes Result.
 *
 * Fehlerquellen, die in `error` münden:
 *   - Param fehlt (Catch-All-Route oder fehlerhafte Konfiguration).
 *   - `TokenError` aus dem Shared-Codec (Format, Länge, Plausibility).
 *
 * Andere Wurf-Quellen (z. B. unerwartete Runtime-Fehler) werden bewusst nicht
 * geschluckt — sie sollen über Error Boundaries sichtbar werden.
 */
export function useTokenFromRoute(options: UseTokenFromRouteOptions = {}): TokenParseResult {
  const params = useParams();
  const rawToken = params[ROUTE_TOKEN_PARAM];
  const { nowMs } = options;
  return useMemo(() => parseToken(rawToken, nowMs), [rawToken, nowMs]);
}

/**
 * Reine Wandlung `string | undefined` → `TokenParseResult`. Ausgelagert, damit
 * Tests den Parser ohne Router-Kontext nutzen können.
 */
function parseToken(rawToken: string | undefined, nowMs: number | undefined): TokenParseResult {
  if (rawToken === undefined || rawToken.length === 0) {
    return { status: 'error' };
  }
  try {
    const decoded = nowMs === undefined ? decodeToken(rawToken) : decodeToken(rawToken, nowMs);
    return { status: 'ok', decoded };
  } catch (cause) {
    if (cause instanceof TokenError) {
      return { status: 'error' };
    }
    throw cause;
  }
}
